import React, { Component } from 'react';
import { cookieService } from '../../helpers/CookieService';
import CookieConsent from "react-cookie-consent";
import "./cookie-consent-styles.css";
import { ifDoNotTrack } from '../../helpers/misc-javascript-helpers';

class CookieConsentDialog extends Component {
    constructor(props) {
        super(props);
        this.onUserAccept = this._onUserAccept.bind(this);
        this.onUserDecline = this._onUserDecline.bind(this);
        this.removeGAOptOutCookie = this._removeGAnalyticsOptOutCookie.bind(this);
        this.checkConsentSettingsOnLoad = this._checkConsentSettingsOnLoad.bind(this);
        this.lastDoNotTrackSetting = this._lastDoNotTrackSetting.bind(this);
        this.onDoNotTrackChanges = this._onDoNotTrackChanges.bind(this);
        this.getHostName = this._getHostName.bind(this);
        this.numDays = 1;
        this.cookies = cookieService;
        this.tracker = 0;
        this.limit = 10;
        this.isProd = process.env.NODE_ENV === 'production' ? true : false;
    }

    componentDidMount() {
        this.lastDoNotTrackSetting();
        this.checkConsentSettingsOnLoad();
    }

    /**
     * Executed the GDPR code at load time.
     * @return void
     */
    _checkConsentSettingsOnLoad = () => {
        const consentCookie = this.cookies.get('skye_learning_cookie_consent');
        if (consentCookie !== undefined && consentCookie !== '') {
            if (consentCookie === 'true') {
                this.onUserAccept();
            } else {
                this.onUserDecline();
            }
        }
        if (consentCookie === undefined && ifDoNotTrack()) {
            this.onUserDecline();
        }
    };

    /** Returns the current domain that is most likely in the relevant cookie for the current Skye stage. */
    _getHostName = () => {
        const stage = {
            prod: /skyelearning.com|www.skyelearning.com/,
            staging: /staging.skyelearning.com/,
        };
        if (process.env.NODE_ENV === 'production' && stage.staging.test(window.location.hostname)) {
            return 'staging.skyelearning.com';
        } else if (process.env.NODE_ENV === 'production' && stage.prod.test(window.location.hostname)) {
            return '.skyelearning.com';
        } else {
            return 'localhost';
        }
    }

    /**
     * 'Do Not Track' browser setting state check, using custom cookie value retention.
     * @return void
     */
    _lastDoNotTrackSetting = () => {
        if (this.getSkyeDntCookieValue() === undefined) {
            this.setSkyeDntCookieValue(ifDoNotTrack().toString());
        } else {
            this.onDoNotTrackChanges();
        }
    }

    /**
     * Simple 'Do Not Track' browser state diffing, if the state has checked from the previous one.
     * @return void
     */
    _onDoNotTrackChanges = () => {
        const previousDntSetting = this.getSkyeDntCookieValue();
        /* If user's do not track is off and it's different from the previous state, then ask for consent again.
            Will take 1 or 2 page loads to have effect, since this is not a SPA and Skye's nav links aren't React routed. */
        if (ifDoNotTrack() !== (previousDntSetting == 'true' ? true : false)) {
            this.setSkyeDntCookieValue(ifDoNotTrack().toString());
            this.removeCookie('skye_learning_cookie_consent', this.getHostName());
        }
    }


    /** Remove Google Analytics Opt-Out Cookie */
    _removeGAnalyticsOptOutCookie = () => {
        this.removeCookie('google-analytics-opt-out', this.getHostName());
    }

    /**
     * Executes the cookie settings code for when a user accepts.
     */
    _onUserAccept = () => {
        this.googleAnalyticsOnAccept();
    }

    /**
     * Executes the cookie settings code for when a user declines.
     */
    _onUserDecline = () => {
        this.googleAnalyticsOnDecline();
        this.miscCookiesOnDecline();
    }

    /**
     * Produces a UTC date string with an added number of days from the current day.
     * @param integer numDays - The number of days to add from the current day.
     * @returns string
     */
    addDays = (numDays) => {
        const date = new Date();
        const addedDays = date.setDate(date.getDate() + numDays);
        return new Date(addedDays).toUTCString();
    }

    /**
     * Produces a UTC date string with a subtracted number of days from the current day.
     * @param integer numDays - The number of days to add from the current day.
     * @returns string
     */
    subDays = (numDays) => {
        const date = new Date();
        const addedDays = date.setDate(date.getDate() - numDays);
        return new Date(addedDays).toUTCString();
    }

    /** 
     * Gets the cookie value for skye-dnt-last-detected-config. This is
     * the last detected setting for the browser's DoNotTrack option.
     * @returns any
     */
    getSkyeDntCookieValue = () => {
        return this.cookies.get('skye-dnt-last-detected-config',
            { path: '/', domain: `${this.getHostName()}` });
    }

    /**
     * Sets skye-dnt-last-detected-config cookie with the browser's DoNotTrack setting.
     */
    setSkyeDntCookieValue = (value) => {
        return this.cookies.set('skye-dnt-last-detected-config', value,
            { path: '/', domain: `${this.getHostName()}` });
    }

    /**
     * Removes a cookie and allows adding different domains to the A : B ternary clauses
     * which is based on if process.env.NODE_ENV === 'production'.
     * @param string cookieName
     * @param string domainValue
     */
    removeCookie = (cookieName, domainValue) => {
        if (this.cookies.get(cookieName) !== undefined) {
            this.cookies.remove(cookieName, {
                expires: 'Thu, 01 Jan 1970 00:00:00 UTC', path: '/',
                domain: domainValue
            });
        }
    }

    /**
     * Removes a cookie based on a name or partial name match.
     * which is based on if process.env.NODE_ENV === 'production'.
     * @param string cookieName
     * @param string domainValue
     */
    removeCookieOnMatch = (cookieName, domainValue) => {
        const pattern = new RegExp(cookieName);
        const allCookieVals = document.cookie.split('; ');
        let matches = allCookieVals.filter(item => pattern.test(item.split('=')[0]));
        matches = matches.map(item => item.split('=')[0]);
        matches.forEach(match => {
            this.removeCookie(match, domainValue);
        });
        return matches;
    }

    /**
     * Handles the gtag() configuration and cookie settings when the user consents to cookies.
     * @returns void
     */
    googleAnalyticsOnAccept = () => {
        if (typeof gtag === 'function' && gtag !== undefined) {
            this.removeGAOptOutCookie();
            // Setting as TRUE - allow ads personalization, demographics and interests reports.
            gtag('set', { 'allow_google_signals': true });
            // Setting as TRUE - turns on ads personalization, llows demographics, and interests reports.
            gtag('set', { 'allow_ad_personalization_signals': true });
            // Setting as TRUE - will send data from page views
            gtag('set', { 'send_page_view': true });
            /* Setting as FALSE => Certain features will be unavailable, including adding users to remarketing lists, 
            adding users to similar audience remarketing seed lists, and related functionality. */
            gtag('set', { 'restricted_data_processing': false });
            // (Using this as a cascade just in case) => Universal Analytics, Google Analytics 4, Google Ads, Floodlight
            gtag('consent', 'update', {
                'ad_storage': 'granted',
                'analytics_storage': 'granted',
                'ads_data_redaction': false,
                'wait_for_update': 500
            });
        }
    }

    /**
     * Handles the gtag() configuration and cookie settings when the user declines cookies.
     * @returns void
     */
    googleAnalyticsOnDecline = () => {
        if (typeof gtag === 'function' && gtag !== undefined) {
            document.cookie = `google-analytics-opt-out=true; expires=${this.addDays(this.numDays)}; path=/; domain=${this.getHostName()}`
            // Setting as TRUE because this turns off ads personalization, demographics and interests reports.
            gtag('set', { 'allow_google_signals': true });
            // Setting as FALSE - turns off ads personalization, BUT ALLOWS => demographics, and interests reports.
            gtag('set', { 'allow_ad_personalization_signals': false });
            // Setting as TRUE - will send data from page views
            gtag('set', { 'send_page_view': true });
            /* Setting as TRUE => Certain features will be AVAILABLE, including adding users to remarketing lists, 
            adding users to similar audience remarketing seed lists, and related functionality. */
            gtag('set', { 'restricted_data_processing': true });
            // (Using this as a cascade just in case) => Universal Analytics, Google Analytics 4, Google Ads, Floodlight.
            gtag('consent', 'update', {
                'ad_storage': 'denied',
                'analytics_storage': 'denied',
                'ads_data_redaction': true,
                'wait_for_update': 500
            });
        }
    }

    /**
     * Adds the statcounter refusal cookie.
     * @returns void
     */
    _statCounterOnDecline = () => {
        document.cookie = `refusal_cookie=1; expires=${this.addDays(this.numDays)}; path=/; domain=${this.getHostName()};`;
    }

    /**
     * Removes the statcounter refusal cookie.
     * @returns void
     */
    _statCounterOnAccept = () => {
        this.cookies.remove('refusal_cookie',
            { path: '/', domain: this.getHostName() });
    }

    /**
     * Neutralizes statcounter measures when the browser DoNotTrack
     * feature is on.
     * @returns void
     */
    _statCounterOnDoNotTrack = () => {
        this.removeCookie('is_unique', '.statcounter.com');
        window.localStorage.removeItem('statcounter.com/localstorage/');
        window.localStorage.removeItem('sc_medium_source');
        window.sessionStorage.removeItem('statcounter_session');
        window.sessionStorage.removeItem('statcounter_config');
    }

    /**
     * Disables cookies that auto-load in Skye Learning, when a user has declined cookies, and the
     * decline occured within the time indicated for the CookieConsent component expires prop.
     */
    miscCookiesOnDecline = (domains = null) => {
        const isStagingOrProd = (this.getHostName() === '.skyelearning.com' ||
            (/staging\.skyelearning\.com/).test(this.getHostName())) ? true : false;

        if ((!domains || domains.length === 0) && isStagingOrProd) {
            domains = ['.skyelearning.com', 'staging.skyelearning.com'];
        }
        if ((!domains || domains.length === 0) && !isStagingOrProd) {
            domains = ['localhost', '.skyelearning.com', 'staging.skyelearning.com'];
        }
        const cookieInterval = setInterval(() => {

            domains.forEach(item => {
                this.removeCookie('hubspotutk', item);
                this.removeCookie('_tt_enable_cookie', item);
                this.removeCookie('__cf_bm', '.hubspot.com');
                this.removeCookie('_ttp', `${isStagingOrProd ? item : '.tiktok.com'}`);
                this.removeCookie('_gcl_au', item);
                this.removeCookie('_fbp', item);
                this.removeCookie('_ga', item);
                this.removeCookie('__hstc', item);
                this.removeCookie('__hssc', item);
                this.removeCookie('__hssrc', item);
                this.removeCookie('fr', '.facebook.com');
                this.removeCookie('IDE', '.doubleclick.net');

                // Remove hotjar cookies.
                this.removeCookieOnMatch('_hjSessionUser_', item);
                this.removeCookieOnMatch('_hjid', item);
                this.removeCookieOnMatch('_hjFirstSeen', item);
                this.removeCookieOnMatch('_hjHasCachedUserAttributes', item);
                this.removeCookieOnMatch('_hjUserAttributesHash', item);
                this.removeCookieOnMatch('_hjDonePolls', item);
                this.removeCookieOnMatch('_hjAbsoluteSessionInProgress', item);
                this.removeCookieOnMatch('_hjSession_', item);
                this.removeCookieOnMatch('_hjIncludedInSessionSample_', item);
                this.removeCookieOnMatch('_hjSessionTooLarge', item);
                this.removeCookieOnMatch('_hjSessionResumed', item);
                this.removeCookieOnMatch('_hjCookieTest', item);
                this.removeCookieOnMatch('_hjSessionStorageTest', item);
                this.removeCookieOnMatch('_hjLocalStorageTest', item);
                this.removeCookieOnMatch('_hjIncludedInPageviewSample', item);
                this.removeCookieOnMatch('_hjIncludedInSessionSample_', item);
                this.removeCookieOnMatch('_hjTLDTest', item);
                this.removeCookieOnMatch('_hjSessionTooLarge', item);
                this.removeCookieOnMatch('_hjSessionResumed', item);
                this.removeCookieOnMatch('_hjClosedSurveyInvites', item);
                this.removeCookieOnMatch('_hjDonePolls', item);
                this.removeCookieOnMatch('_hjMinimizedPolls', item);
                this.removeCookieOnMatch('_hjShownFeedbackMessage', item);
                window.localStorage.removeItem('hubspotutk');

                // Clear the relevant localStorage or sessionStorage items if user has 'doNotTrack' engaged.
                if (ifDoNotTrack()) {
                    window.sessionStorage.removeItem('tt_sessionId');
                    window.sessionStorage.removeItem('tt_pixel_session_index');
                    window.sessionStorage.removeItem('tt_appInfo');
                }
            });

            if (this.tracker >= this.limit) {
                clearInterval(cookieInterval);
            }
            this.tracker++;

        }, 2000);
    }

    render() {
        const numDays = this.numDays;
        return (

            <CookieConsent
                enableDeclineButton
                location="bottom"
                buttonText="Allow Cookies"
                declineButtonText="Decline"
                cookieName="skye_learning_cookie_consent"
                style={{ background: '#2B373B' }}
                buttonStyle={{ color: '#4e503b', fontSize: '13px' }}
                expires={numDays}
                onAccept={() => { this.onUserAccept(); }}
                onDecline={() => { this.onUserDecline(); }}
            >
                <div className="cc-text" tabIndex="0">
                    <p>This website uses cookies to ensure you get the best experience on our website. <a className="cc-learn-more-link text-bold" href="/privacy">Learn more.</a></p>
                </div>
            </CookieConsent>

        );
    }
}

export default CookieConsentDialog;